import ordersApi from '@/api/ordersApi'

// Items a customer actually sees in the cart (auto/donation lines are dropped by reorder anyway).
export const visibleCartItems = (items) =>
  (items || []).filter((item) => item.should_show_in_cart !== false && item.item_type !== 'donation')

// `deleted` / `changed` come back as raw order lines, modifiers included. Only the top-level
// lines are products the customer would recognise; fall back to every line if none are marked.
const summarizeDiffLines = (lines) => {
  const all = Array.isArray(lines) ? lines : []
  const topLevel = all.filter((line) => !line.parent_itemno && !line.parent_orderitem)
  return (topLevel.length ? topLevel : all).map((line) => ({
    itemId: line.itemid ?? line.item_id,
    name: line.itemname ?? line.item_name,
    size: line.size_name ?? line.size?.name ?? null,
    quantity: line.quantity,
  }))
}

// POST /orders/new?test_edit=true answers [orderId, { deleted, changed }] without touching the
// customer's cart. The order it creates is a throwaway, but reading it tells us whether anything
// would be left in the cart at all.
export const fetchReorderDiff = async (payload, baseItems) => {
  const response = await ordersApi.createOrderDiff(payload)
  const data = response?.data?.data
  if (!Array.isArray(data)) {
    throw new Error('Unexpected reorder diff response')
  }
  const [diffOrderId, diff] = data
  const deleted = summarizeDiffLines(diff?.deleted)
  const changed = summarizeDiffLines(diff?.changed)

  let allUnavailable = false
  if (deleted.length > 0) {
    try {
      const preview = await ordersApi.getOrder(diffOrderId)
      allUnavailable = visibleCartItems(preview.data.data?.order?.items).length === 0
    } catch (error) {
      console.log(error)
      allUnavailable = deleted.length >= visibleCartItems(baseItems).length
    }
  }

  return {
    deleted,
    changed,
    allUnavailable,
    hasChanges: deleted.length > 0 || changed.length > 0,
  }
}

// Cart lines that match a deleted entry. Duplicate products are matched one line per entry,
// preferring the line with the same quantity.
export const matchUnavailableCartItems = (cartItems, deleted) => {
  const remaining = [...(deleted || [])]
  const unavailableIds = []
  visibleCartItems(cartItems).forEach((item) => {
    let index = remaining.findIndex((entry) => entry.itemId == item.item_id && entry.quantity == item.quantity)
    if (index === -1) {
      index = remaining.findIndex((entry) => entry.itemId == item.item_id)
    }
    if (index !== -1) {
      unavailableIds.push(item.order_item_id)
      remaining.splice(index, 1)
    }
  })
  return unavailableIds
}

export const formatDiffItem = (entry) => {
  let label = entry.name
  if (entry.size) label += ` (${entry.size})`
  if (entry.quantity > 1) label += ` x${entry.quantity}`
  return label
}

// Store/time context a reorder rewrites before the customer has confirmed anything.
const CONTEXT_STORAGE_KEYS = [
  'selectedStoreId',
  'deliveryInfo',
  'selectedServiceId',
  'selectedServiceData',
  'selectedDay',
  'selectedHour',
  'selectedOrderType',
  'selectedStoreDays',
  'selectedAddress',
  'timeZoneOffset',
]

export const snapshotOrderContext = (store) => ({
  storage: CONTEXT_STORAGE_KEYS.reduce((acc, key) => {
    acc[key] = localStorage.getItem(key)
    return acc
  }, {}),
  selectedStore: store.state.selectedStore,
  selectedService: store.state.selectedService,
  selectedStoreDays: store.state.selectedStoreDays,
  selectedDay: store.state.selectedDay,
  selectedHour: store.state.selectedHour,
  selectedAddress: store.state.selectedAddress,
})

export const restoreOrderContext = (store, snapshot) => {
  if (!snapshot) return
  store.commit('SET_SELECTED_STORE', snapshot.selectedStore)
  store.commit('SET_SELECTED_SERVICE', snapshot.selectedService)
  store.commit('SET_SELECTED_STORE_DAYS', snapshot.selectedStoreDays)
  store.commit('SET_SELECTED_DAY', snapshot.selectedDay)
  store.commit('SET_SELECTED_HOUR', snapshot.selectedHour)
  store.commit('SET_SELECTED_ADDRESS', snapshot.selectedAddress)
  // after the commits: SET_SELECTED_SERVICE writes selectedServiceData itself
  Object.entries(snapshot.storage).forEach(([key, value]) => {
    if (value === null) {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, value)
    }
  })
}
